import { useQuery } from '@tanstack/react-query';
import { subDays } from 'date-fns';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { DateRangePicker } from 'src/components/DateRangePicker';
import { ListItem } from 'src/components/ListItem';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { Package } from 'src/models/package/_types';
import { fetchPackageOptions } from 'src/models/package/package';
import { dateToTimestamp, timestampToDate } from 'src/utils/fieldNormalizers';

import { LocationSelect } from '../Forms/Selects/LocationSelect';
import { ServiceSelect } from '../Forms/Selects/ServiceSelect';

/*
  This filter has a lot of options and special cases.
  Backend endpoints expect 'q[client_filter]' to be a 5-tuple
  [value, (number | packageId | locationId), dateStart, dateEnd, platformId]
*/

interface ClientCustomStatusFilterValue {
  // The main "value" for this option
  value: string;
  // `needsNumber` will set the input number
  // `needsPackage` will set the package id as number
  // `needsLocation` will set the location id as number
  // `needsService` will set the service id as number
  // Only one of these options can be used at a time
  number?: string;
  // `needsDateRange` will set start & end.
  // The backend expects them even they arent used, so we send default values
  start: string;
  end: string;
}

interface ClientCustomStatusFilterProps {
  field: string;
  label: string;
  value: any;
  setValue: (newVal: ClientCustomStatusFilterValue | null) => void;
  valueOnly?: boolean;
}

interface OptionConfig {
  label: string;
  val: string | null;
  description?: string;
  tooltip?: string;
  needsDateRange?: boolean;
  needsNumber?: boolean;
  numberLabel?: string;
  needsPackage?: boolean;
  needsLocation?: boolean;
  needsService?: boolean;
}

const customFilterOptions = [
  { label: 'Any', val: null },
  { label: 'Logged a Workout', val: 'logged_workout' },
  { label: 'No Logged Workout', val: 'no_workouts' },
  {
    label: 'No Recently Logged Workout',
    val: 'not_logged_workout',
    description: 'Not logged a workout during this time frame.',
    needsDateRange: true,
  },
  {
    label: 'Has Unsigned Waiver',
    val: 'has_unsigned_waiver',
    description: 'Has an unsigned waiver.',
  },
  { label: `Plan Assigned`, val: 'plan_assigned' },
  {
    label: `No Plan Assigned`,
    val: 'no_plan_assigned',
  },
  {
    label: `Scheduled on a Plan`,
    val: 'scheduled_plan',
  },
  {
    label: `Week Plan Ending Soon`,
    val: 'ending_soon',
    description: 'Has 7 or fewer workouts left on week plan.',
  },
  {
    label: `Calendar Plan Ending Soon`,
    val: 'calendar_plan_ending',
    description: `A Plan on their calendar ending during this time frame.`,
    needsDateRange: true,
  },
  {
    label: `Calendar Ending Soon`,
    val: 'calendar_ending',
    description: `The last workout in the calendar is within this time frame.`,
    needsDateRange: true,
  },
  {
    label: `Assessment Assigned`,
    val: 'assessment_sent',
  },
  {
    label: `Assessment Completed`,
    val: 'assessment_completed',
  },
  {
    label: `Assessment Completed Recently`,
    val: 'completed_assessment',
    description: `Completed an assessment in the time frame.`,
    needsDateRange: true,
  },
  { label: 'Failed Payments', val: 'failed_payment' },
  {
    label: 'Recently Failed Payments',
    val: 'failed_payments',
    description: 'Payment Failed during this time frame.',
    needsDateRange: true,
  },
  {
    label: 'Subscription Contract Ending Soon',
    val: 'subscription_ending_soon',
    needsNumber: true,
    numberLabel: 'Payments Left',
    description: 'Less than or equal to',
  },
  {
    label: 'Card Expiring Soon',
    val: 'expiring_card',
    description: 'Credit card is expiring during this time frame.',
    needsDateRange: true,
  },
  {
    label: `Package Expiring Soon`,
    val: 'expiring_package',
    description: `Package is expiring during this time frame.`,
    needsDateRange: true,
    needsPackage: true,
  },
  {
    label: `Owns This Package`,
    val: 'owned_package',
    needsPackage: true,
  },
  {
    label: `Managers With Dependant That Owns This Package`,
    val: 'dependent_owns_packages',
    needsPackage: true,
  },
  {
    label: `Owns This Location`,
    val: 'owned_location',
    needsLocation: true,
  },
  {
    label: 'Has Visited This Service',
    val: 'owned_service',
    needsService: true,
  },
  {
    label: 'No Recent Visits',
    val: 'no_visit',
    description: 'Has not had a session during this time frame.',
    needsDateRange: true,
  },
  { label: 'Birthday', val: 'birthday_today', needsDateRange: true },
  {
    label: 'Signup Yearly Anniversary',
    val: 'signup_date',
    needsNumber: true,
    numberLabel: 'Years',
    needsDateRange: true,
    tooltip:
      'Enter a number. only users who have been signed up for that many years in the range specified below will show up',
  },
  {
    label: 'Signup Month Milestone',
    val: 'signup_date_month',
    needsNumber: true,
    numberLabel: 'Months',
    needsDateRange: true,
    tooltip:
      'Enter a number. only users who have been signed up for that many months in the range specified below will show up',
  },
  {
    label: `New Client`,
    val: 'new_clients',
    description: `New client during this time frame.`,
    needsDateRange: true,
  },
  { label: 'Current In-App Purchase Subscription', val: 'in_app_purchase' },
  { label: 'Payment Method on File', val: 'has_payment_method' },
  { label: 'No Payment Method on File', val: 'has_no_payment_method' },
  { label: 'Credit Card on File', val: 'credit_card_on_file' },
  { label: 'Bank Account on File', val: 'bank_acc_on_file' },
  { label: 'Paused Subscription', val: 'has_paused_subscription' },
  { label: 'Negative Service Balance', val: 'negative_service_balance' },
  { label: 'Positive Service Balance', val: 'positive_service_balance' },
  { label: 'Any Service Balance', val: 'any_service_balance' },
  { label: 'Positive Account Balance', val: 'positive_account_balance' },
  { label: 'Negative Account Balance', val: 'negative_account_balance' },
  { label: 'Any Account Balance', val: 'any_account_balance' },
  { label: `Is Dependent`, val: 'has_dependents' },
  { label: `Is Manager`, val: 'has_managers' },
  { label: `New Messages`, val: 'new_messages', needsDateRange: true },
].sort((a, b) => a.label.localeCompare(b.label)) satisfies OptionConfig[];

const today = new Date();
const defaultStart = subDays(today, today.getDay() + 7);
const defaultEnd = today;

export const ClientCustomStatusFilter = ({
  label,
  setValue,
  value,
  valueOnly,
}: ClientCustomStatusFilterProps) => {
  const setMainValue = (newValue) => {
    if (!newValue) {
      setValue(null);
      return;
    }
    setValue({
      value: newValue.val,
      start: String(dateToTimestamp(defaultStart)),
      end: String(dateToTimestamp(defaultEnd)),
    });
  };

  const setDateRange = (start, end) => {
    setValue({
      ...value,
      start,
      end,
    });
  };

  const setNumber = (newNumber) => {
    setValue({
      ...value,
      number: newNumber,
    });
  };

  const currentlySelectedOption =
    customFilterOptions?.find((option) => option?.val === value?.value) || null;

  if (valueOnly) {
    return <>{currentlySelectedOption?.label}</>;
  }

  return (
    <Box>
      <Autocomplete
        options={customFilterOptions}
        getOptionLabel={(option) => option.label}
        value={currentlySelectedOption}
        onChange={(_, newValue) => {
          setMainValue(newValue);
        }}
        renderInput={(params) => (
          <TextField {...params} label={label} placeholder="Any" />
        )}
        renderOption={(props, option) => {
          return (
            <ListItem
              {...(props as any)}
              clickable
              primary={option.label}
              key={option.val}
            />
          );
        }}
      />

      {!!currentlySelectedOption?.needsNumber && (
        <TextField
          type="number"
          value={value.number}
          label={currentlySelectedOption?.numberLabel || 'Number'}
          onChange={(e) => {
            setNumber(e.target.value);
          }}
          sx={{ mt: 2 }}
        ></TextField>
      )}

      {!!currentlySelectedOption?.needsPackage && (
        <PackageSelect
          value={value.number}
          setValue={(newNumber) => {
            setNumber(newNumber);
          }}
        />
      )}

      {!!currentlySelectedOption?.needsLocation && (
        <LocationSelect
          value={value.number}
          onChange={(newValue) => setNumber(newValue)}
          label="Location"
          includeNoLocation
          sx={{ mt: 2 }}
        />
      )}

      {!!currentlySelectedOption?.needsService && (
        <ServiceSelect
          value={value.number}
          onChange={(newValue) => setNumber(newValue)}
          label="Service"
          sx={{ mt: 2 }}
        />
      )}

      {!!currentlySelectedOption?.tooltip && (
        <Typography color="text.secondary" variant="body2" sx={{ mt: 2 }}>
          {currentlySelectedOption?.tooltip}
        </Typography>
      )}

      {!!currentlySelectedOption?.needsDateRange && (
        <DateRangePicker
          multiInput
          value={[
            value?.start ? timestampToDate(value?.start) : defaultStart,
            value?.end ? timestampToDate(value?.end) : defaultEnd,
          ]}
          onChange={(newValue) => {
            if (newValue[0] && newValue[1]) {
              setDateRange(
                dateToTimestamp(newValue[0]),
                dateToTimestamp(newValue[1])
              );
            }
          }}
          label={''}
          sx={{ mt: 2 }}
        ></DateRangePicker>
      )}

      {!!currentlySelectedOption?.description && (
        <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
          {currentlySelectedOption?.description}
        </Typography>
      )}
    </Box>
  );
};

const ANY_PACKAGE_OPTION = { id: -2, name: 'Any Package' } as Package;

interface PackageSelectProps {
  value: any;
  setValue: (newVal: any) => void;
}
export const PackageSelect: React.FC<PackageSelectProps> = ({
  value,
  setValue,
}) => {
  const { data: packages, isLoading } = useQuery({
    queryKey: ['packages'],
    queryFn: () => {
      return fetchPackageOptions();
    },
    staleTime: 60000,
  });

  const options = packages ? [ANY_PACKAGE_OPTION, ...packages] : [ANY_PACKAGE_OPTION];

  const selectedPackage = options?.find((group) => group.id === value) || null;

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(pkg: Package) => pkg?.name || 'Loading'}
      value={selectedPackage}
      onChange={(_, newValue: Package) => setValue(newValue?.id)}
      loading={isLoading}
      renderInput={(params) => (
        <TextField {...params} label={'Package'} placeholder="Package" />
      )}
      sx={{ mt: 2 }}
      renderOption={(props, group) => {
        return (
          <ListItem
            {...(props as any)}
            clickable
            primary={group.name}
            key={group.name + group.id}
          />
        );
      }}
    />
  );
};
